import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listReplays, startReplay, stopReplay, replayCommand,
  replayStreamUrl, replayTelemetryUrl, fetchReplaySchedule, downloadReplay,
} from '../lib/api';
import LiveTrackMap from '../components/LiveTrackMap';
import LiveTimingTable from '../components/LiveTimingTable';
import LiveTelemetry from '../components/LiveTelemetry';

const SPEEDS = [1, 2, 5, 10, 20, 60];

/* Authored transport glyphs. The old UI used U+25B6 / U+275A characters,
   which fall back to whatever the platform happens to carry — the repo
   already went through "the last of the glyphs" once. */
function IconPlay() {
  return (
    <svg className="ic" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M4 2.4 13 8l-9 5.6z" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg className="ic" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M4 2.6h3.1v10.8H4zM8.9 2.6H12v10.8H8.9z" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconStop() {
  return (
    <svg className="ic" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3.4 3.4h9.2v9.2H3.4z" fill="currentColor" stroke="none" />
    </svg>
  );
}
const STAGE_TEXT = {
  reading: 'Reading the recording',
  outline: 'Extracting the track outline',
  building: 'Building the session state',
  seeking: 'Seeking to the green flag',
  playing: 'Playing',
  error: 'Could not start this recording',
};

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return i === 0 ? `${v} ${units[i]}` : `${v.toFixed(1)} ${units[i]}`;
}

function fmtDuration(sec) {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtClock(sec) {
  if (sec == null || !Number.isFinite(sec)) return '0:00';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    : `${m}:${String(r).padStart(2, '0')}`;
}

/* ── mobile breakpoint ─────────────────────────────────────────────── */
function useNarrow(max = 900) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${max}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${max}px)`);
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [max]);
  return narrow;
}

/* ══════════════════════════════════════════════════════════════════════
   Library — pick a recording, or pull a new one down
   ══════════════════════════════════════════════════════════════════ */
function Library({ replays, loading, error, busy, onPlay, onRefresh, onDownloaded }) {
  const [tab, setTab] = useState('library');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [schedule, setSchedule] = useState(null);
  const [schedLoading, setSchedLoading] = useState(false);
  const [round, setRound] = useState('');
  const [session, setSession] = useState('');
  const [dlError, setDlError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (tab !== 'download') return;
    let alive = true;
    setSchedLoading(true);
    setRound('');
    setSession('');
    fetchReplaySchedule(year)
      .then((d) => { if (alive) setSchedule(d); })
      .catch(() => { if (alive) setSchedule({ events: [] }); })
      .finally(() => { if (alive) setSchedLoading(false); });
    return () => { alive = false; };
  }, [tab, year]);

  const roundObj = useMemo(
    () => schedule?.events?.find((e) => String(e.round) === String(round)),
    [schedule, round]
  );

  const doDownload = async () => {
    if (!round || !session) { setDlError('Pick an event and a session.'); return; }
    setDownloading(true);
    setDlError(null);
    try {
      await downloadReplay({ year: Number(year), round: Number(round), session });
      await onDownloaded();
      setTab('library');
    } catch (e) {
      setDlError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pv-lib">
      {/* The tablist wraps ONLY tabs — a tablist whose children include a
          spacer or a toolbar button is an aria-required-children violation. */}
      <div className="tabs">
        <div role="tablist" aria-label="Replay library" style={{ display: 'flex' }}>
          <button className={`tab ${tab === 'library' ? 'active' : ''}`} role="tab"
            aria-selected={tab === 'library'} onClick={() => setTab('library')}>Library</button>
          <button className={`tab ${tab === 'download' ? 'active' : ''}`} role="tab"
            aria-selected={tab === 'download'} onClick={() => setTab('download')}>Download</button>
        </div>
        <span style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={onRefresh} disabled={loading}
          style={{ alignSelf: 'center' }}>Refresh</button>
      </div>

      {error && <div className="alert alert--err" role="alert"><b>Offline</b><span>{error}</span></div>}

      {tab === 'library' && (
        <>
          {loading && <div className="skeleton" style={{ height: 96 }} />}
          {!loading && replays.length === 0 && (
            <div className="empty">
              <b>No recordings yet</b>
              <p>Switch to Download to pull a past session down from F1’s archive.
                 A race is about 20 MB and takes a few seconds.</p>
            </div>
          )}
          {!loading && replays.length > 0 && (
            <div className="pv-lib-grid">
              {replays.map((r) => (
                <article key={r.name} className="pv-card">
                  <h2 className="pv-card-title">{r.event_name || r.name}</h2>
                  <div className="pv-card-meta">
                    <span>{r.year || '—'} · {r.session_name || 'Session'}</span>
                    <span>{fmtDuration(r.duration_sec)}</span>
                    <span>{fmtSize(r.size)}</span>
                    {r.record_count ? <span>{r.record_count.toLocaleString()} records</span> : null}
                  </div>
                  <button className="btn" onClick={() => onPlay(r)} disabled={busy}>
                    {busy ? 'Starting…' : 'Play replay'}
                  </button>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'download' && (
        <section className="panel">
          <div className="pv-fields">
            <label className="field pv-field">
              <span className="pv-flabel">Year</span>
              <select className="select" value={year} onChange={(e) => setYear(e.target.value)} disabled={downloading}>
                {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>

            <label className="field pv-field">
              <span className="pv-flabel">Event</span>
              <select className="select" value={round} onChange={(e) => { setRound(e.target.value); setSession(''); }}
                disabled={downloading || schedLoading || !schedule?.events?.length}>
                <option value="">{schedLoading ? 'Loading…' : 'Select a grand prix'}</option>
                {schedule?.events?.map((e) => (
                  <option key={e.round} value={e.round}>R{e.round} · {e.event_name}</option>
                ))}
              </select>
            </label>

            <label className="field pv-field">
              <span className="pv-flabel">Session</span>
              <select className="select" value={session} onChange={(e) => setSession(e.target.value)}
                disabled={downloading || !roundObj}>
                <option value="">Select a session</option>
                {roundObj?.sessions?.filter((s) => s.past).map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
                {roundObj && !roundObj.sessions?.some((s) => s.past) && (
                  <option value="" disabled>No session has run yet</option>
                )}
              </select>
            </label>
          </div>

          <p className="pv-catblurb">
            Only sessions that have already happened can be downloaded. The file comes
            straight from F1’s public archive (roughly 15–25 MB for a race) and is
            converted to a timeline once — after that it plays offline from here.
          </p>

          {dlError && <div className="alert alert--err" role="alert"><b>Error</b><span>{dlError}</span></div>}

          <button className="btn pv-run" onClick={doDownload}
            disabled={downloading || !round || !session}>
            {downloading ? 'Downloading…' : 'Download session'}
          </button>
        </section>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Page
   ══════════════════════════════════════════════════════════════════ */
export default function Replays() {
  const [replays, setReplays] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const [session, setSession] = useState(null);   // {sessionId, file, speed, duration_sec}
  const [snap, setSnap] = useState(null);
  const [selected, setSelected] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [telemetry, setTelemetry] = useState({});
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [seeking, setSeeking] = useState(false);
  const [tab, setTab] = useState('track');
  // Once a session has actually started playing, a later non-playing
  // stage means a SEEK is rebuilding state — not a cold start — and must
  // not blank the player back to a full-screen loader.
  const [everPlayed, setEverPlayed] = useState(false);

  const narrow = useNarrow();
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const stage0 = snap?.load?.stage || 'reading';
  const playing0 = stage0 === 'playing';
  useEffect(() => { if (playing0) setEverPlayed(true); }, [playing0]);

  const refresh = useCallback(() => {
    setListLoading(true);
    setListError(null);
    listReplays()
      .then((d) => setReplays(d.replays || []))
      .catch((e) => setListError(e.message))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  /* Snapshot stream ------------------------------------------------- */
  useEffect(() => {
    if (!session) return undefined;
    let es;
    try {
      es = new EventSource(replayStreamUrl(session.sessionId));
    } catch (_) {
      return undefined;
    }
    es.addEventListener('snapshot', (e) => {
      try { setSnap(JSON.parse(e.data)); } catch (_) { /* ignore */ }
    });
    es.onerror = () => { /* EventSource retries on its own */ };
    return () => es.close();
  }, [session]);

  /* Telemetry stream ------------------------------------------------ */
  useEffect(() => {
    if (!session || selected.length === 0) { setTelemetry({}); return undefined; }
    let es;
    try {
      es = new EventSource(replayTelemetryUrl(session.sessionId, selected));
    } catch (_) {
      return undefined;
    }
    es.addEventListener('telemetry', (e) => {
      try {
        const payload = JSON.parse(e.data);
        setTelemetry(payload.telemetry || {});
      } catch (_) { /* ignore */ }
    });
    return () => es.close();
  }, [session, selected]);

  /* Stop the session when leaving ---------------------------------- */
  useEffect(() => () => {
    const s = sessionRef.current;
    if (s) stopReplay(s.sessionId).catch(() => {});
  }, []);

  const play = async (rec) => {
    setStarting(true);
    setStartError(null);
    try {
      if (session) { await stopReplay(session.sessionId).catch(() => {}); }
      const j = await startReplay({ name: rec.name, speed, loop: true });
      setSnap(null);
      setSelected([]);
      setPaused(false);
      setEverPlayed(false);
      setSession({
        sessionId: j.session_id,
        file: j.file,
        speed: j.speed,
        durationSec: j.duration_sec || rec.duration_sec || 0,
      });
    } catch (e) {
      setStartError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const backToLibrary = async () => {
    if (session) { await stopReplay(session.sessionId).catch(() => {}); }
    setSession(null);
    setSnap(null);
    setEverPlayed(false);
    setTelemetry({});
    setSelected([]);
    refresh();
  };

  const command = async (name, payload) => {
    if (!session) return;
    try { await replayCommand(session.sessionId, name, payload); } catch (_) { /* transient */ }
  };

  const togglePause = () => {
    command(paused ? 'resume' : 'pause');
    setPaused((p) => !p);
  };

  const changeSpeed = (v) => { setSpeed(v); command('speed', { speed: v }); };

  const onSeek = (e) => {
    if (!session?.durationSec) return;
    setSeeking(true);
    command('seek', { t_sec: Number(e.target.value) });
  };

  const toggleDriver = (num) => {
    setSelected((s) => (s.includes(num) ? s.filter((x) => x !== num) : [...s, num].slice(0, 4)));
  };

  const stage = snap?.load?.stage || 'reading';
  const playing = stage === 'playing';
  const elapsed = snap?.session?.elapsed_sec ?? 0;
  const duration = session?.durationSec || 0;
  const drivers = useMemo(() => (snap?.drivers || []).map((d) => ({ ...d, position: d.position ?? d.line })), [snap]);

  /* ── Library ─────────────────────────────────────────────────────── */
  if (!session) {
    return (
      <div className="pv-replay">
        <header className="pv-page-head">
          <h1>Replays</h1>
          <span className="meta">
            {replays.length} recording{replays.length === 1 ? '' : 's'} · play at your own speed
          </span>
        </header>
        {startError && <div className="alert alert--err" role="alert"><b>Could not start</b><span>{startError}</span></div>}
        <Library
          replays={replays} loading={listLoading} error={listError} busy={starting}
          onPlay={play} onRefresh={refresh} onDownloaded={refresh}
        />
      </div>
    );
  }

  /* ── Player ──────────────────────────────────────────────────────── */
  const trackPanel = (
    <section className="panel">
      <div className="pv-panel-head"><span>Track</span>
        <span className="meta">{snap?.session?.name || '—'}</span></div>
      <LiveTrackMap
        outline={snap?.session?.track_outline}
        corners={snap?.session?.corners}
        rotation={snap?.session?.track_rotation}
        drivers={drivers}
        selected={selected}
        hovered={hovered}
        onToggle={toggleDriver}
        onHover={setHovered}
      />
    </section>
  );

  const timingPanel = (
    <section className="panel">
      <div className="pv-panel-head"><span>Timing</span>
        <span className="meta">{drivers.length} cars</span></div>
      <LiveTimingTable drivers={drivers} selected={selected}
        onToggle={toggleDriver} onHover={setHovered} />
    </section>
  );

  const controlPanel = (
    <>
      <section className="panel">
        <div className="pv-panel-head"><span>Race control</span></div>
        {snap?.race_control?.length ? (
          <div>
            {snap.race_control.slice(-8).reverse().map((m, i) => (
              <div key={i} className="logline">
                <time>{(m.Utc || '').slice(11, 19) || '—'}</time>
                <span className="lmsg">{m.Message}</span>
              </div>
            ))}
          </div>
        ) : <p className="pv-hint">No messages yet.</p>}
      </section>
      <section className="panel">
        <div className="pv-panel-head"><span>Weather</span></div>
        {snap?.weather && Object.keys(snap.weather).length ? (
          <div className="pv-info">
            {[
              ['Air', snap.weather.Air_Temp != null ? `${snap.weather.Air_Temp}°C` : '—'],
              ['Track', snap.weather.Track_Temp != null ? `${snap.weather.Track_Temp}°C` : '—'],
              ['Humidity', snap.weather.Humidity != null ? `${snap.weather.Humidity}%` : '—'],
              ['Wind', snap.weather.Wind_Speed != null ? `${snap.weather.Wind_Speed} m/s` : '—'],
              ['Rainfall', snap.weather.Rainfall != null ? String(snap.weather.Rainfall) : '—'],
            ].map(([k, v]) => (
              <div key={k} className="pv-info-row">
                <span className="pv-info-key">{k}</span>
                <span className="pv-info-val">{v}</span>
              </div>
            ))}
          </div>
        ) : <p className="pv-hint">Waiting for the first weather sample.</p>}
      </section>
    </>
  );

  const telemetryPanel = (
    <section className="panel">
      <div className="pv-panel-head">
        <span>Telemetry</span>
        <span className="meta">{selected.length ? `${selected.length} selected` : 'tap a row or a dot'}</span>
      </div>
      {selected.length === 0 ? (
        <div className="empty" style={{ padding: 'var(--s-4) 0' }}>
          <b>No driver selected</b>
          <p>Tap a driver on the map or a row in the timing table to plot their telemetry.</p>
        </div>
      ) : (
        <>
          <div className="pv-legend" style={{ marginBottom: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={() => setSelected([])}>Clear</button>
          </div>
          <LiveTelemetry
            selected={selected}
            drivers={Object.fromEntries(drivers.map((d) => [d.number, d]))}
            telemetry={telemetry}
            allDrivers={drivers}
            onToggle={toggleDriver}
            onSelectAll={() => setSelected(drivers.slice(0, 4).map((d) => d.number))}
            onClearAll={() => setSelected([])}
          />
        </>
      )}
    </section>
  );

  const TABS = [
    { id: 'track', label: 'Track' },
    { id: 'timing', label: 'Timing' },
    { id: 'control', label: 'Control' },
    { id: 'telemetry', label: 'Telemetry' },
  ];

  return (
    <div className="pv-replay">
      {/* transport — the session name is this page's h1 */}
      <div className="pv-transport">
        <div className="pv-transport-name">
          <h1>{snap?.session?.name || session.file}</h1>
          <span>
            {!playing && !everPlayed ? (STAGE_TEXT[stage] || 'Loading')
              : !playing ? 'Seeking…'
              : paused ? 'Paused' : `${speed}× replay`}
            {snap?.session?.lap ? ` · Lap ${snap.session.lap}${snap.session.total_laps ? `/${snap.session.total_laps}` : ''}` : ''}
          </span>
        </div>

        <div className="pv-seek">
          <span className="pv-time">{fmtClock(elapsed)}</span>
          <input className="range" type="range" min="0"
            max={Math.max(1, Math.floor(duration))} step="1"
            value={Math.floor(elapsed)}
            onChange={onSeek}
            onPointerDown={() => setSeeking(true)}
            onPointerUp={() => setSeeking(false)}
            aria-label="Seek within the recording"
            disabled={!duration} />
          <span className="pv-time">{fmtClock(duration)}</span>
        </div>

        <div className="pv-transport-actions">
          <button className="btn btn--ghost" onClick={togglePause} disabled={!playing}>
            {paused ? <><IconPlay /> Resume</> : <><IconPause /> Pause</>}
          </button>
          <span className="pv-speed">
            <span className="pv-speed-label" aria-hidden="true">Speed</span>
            <select className="pv-speed-select" aria-label="Replay speed"
              value={speed} onChange={(e) => changeSpeed(Number(e.target.value))}
              disabled={!playing}>
              {SPEEDS.map((s) => <option key={s} value={s}>{s}×</option>)}
            </select>
          </span>
          <button className="btn btn--ghost" onClick={backToLibrary}>
            <IconStop /> Library
          </button>
        </div>
      </div>

      {!playing && !everPlayed && (
        <div className="panel">
          <div className="pv-stage">
            <div className="pv-stage-title">{STAGE_TEXT[stage] || 'Loading'}</div>
            <div className="bar bar--scan" style={{ width: 'min(420px, 100%)' }}>
              <span className="fill" style={{ width: '100%' }} />
            </div>
            <p className="pv-stage-note">
              {snap?.load?.detail || 'Reading the recording and building the session.'}
              {' '}First time on this machine takes about half a minute; it is cached after that.
            </p>
            <button className="btn btn--ghost" onClick={backToLibrary}>Cancel</button>
          </div>
        </div>
      )}

      {(playing || everPlayed) && (
        <>
          {narrow ? (
            <>
              <div className="pv-tabs" role="tablist" aria-label="Replay views">
                {TABS.map((t) => (
                  <button key={t.id} className="pv-tab" role="tab"
                    aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
              {tab === 'track' && trackPanel}
              {tab === 'timing' && timingPanel}
              {tab === 'control' && controlPanel}
              {tab === 'telemetry' && telemetryPanel}
            </>
          ) : (
            <>
              <div className="pv-grid">
                <div className="pv-grid-col">{trackPanel}</div>
                <div className="pv-grid-col">{timingPanel}</div>
                <div className="pv-grid-col">{controlPanel}</div>
              </div>
              {telemetryPanel}
            </>
          )}
        </>
      )}

      {seeking && <span className="sr-only" aria-live="polite">Seeking</span>}
    </div>
  );
}
